import React from 'react';
import { Button } from '@/components/ui/button';
import { useConnectionStatus } from '@/shared/hooks/useConnectionStatus';
import { UserPlus, Clock, Check, UserMinus, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

interface ConnectionButtonProps {
  targetUserId: string;
  className?: string;
  onConnectionChange?: () => void;
}

export const ConnectionButton: React.FC<ConnectionButtonProps> = ({ 
  targetUserId, 
  className,
  onConnectionChange 
}) => {
  const { status, loading, error, sendRequest, refetch } = useConnectionStatus(targetUserId);
  const { t } = useI18n();

  const handleDisconnect = async () => {
    try {
      const { error } = await supabase.rpc('remove_connection', { target_user: targetUserId });
      if (error) {
        console.error('Erro ao remover conexão:', error);
        return;
      }
      await refetch();
      if (onConnectionChange) {
        onConnectionChange();
      }
    } catch (error) {
      console.error('Erro ao processar remoção de conexão:', error);
    }
  };

  if (status === 'connected') {
    return (
      <div className="w-full">
        <CustomDropdownMenu
          trigger={
            <Button
              variant="secondary"
              className={cn(className, 'w-full py-1.5 h-auto bg-[#7F7F7F] border-0')}
            >
              {t('connection.connected')}
            </Button>
          }
          items={[{
            label: t('connection.disconnect'),
            icon: <UserMinus className="h-4 w-4 mr-2 text-white" />,
            onClick: handleDisconnect,
            className: "text-white hover:text-white"
          }]}
        />
      </div>
    );
  }

  if (status === 'pending' || status === 'pending_sent' || status === 'pending_received') {
    const isPendingSent = status === 'pending_sent';
    const isPendingReceived = status === 'pending_received';

    return (
      <div className="w-full">
        <Button
          variant="secondary"
          disabled
          className={cn(className, 'w-full py-1.5 h-auto bg-[#4A403E] text-gray-300 border-0 disabled:opacity-100')}
          style={{boxShadow: 'none'}}
        >
          {isPendingSent
            ? t('connection.requested') // "Requested" - I sent it
            : isPendingReceived
            ? t('connection.pending') // "Pending" - I received it
            : t('connection.requested') // Fallback
          }
        </Button>
      </div>
    );
  }

  // status === 'none'
  return (
    <div className="w-full">
      <Button
        variant="primary"
        onClick={sendRequest}
        disabled={loading}
        className={cn(className, 'w-full py-1.5 h-auto border-0')}
        style={{boxShadow: 'none'}}
      >
        {loading ? t('connection.sending') : t('connection.connect')}
      </Button>
    </div>
  );
}; 